import clsx from "clsx";
import { useState } from "react";

import MemberPermissionsTag from "common/components/MemberPermissionsTag";
import AimfoxAvatar from "common/components/ui/AimfoxAvatar";
import { Dialog, DialogContent } from "common/components/ui/Dialog";
import ProfileImage from "common/components/ui/ProfileImage";
import {
  Table,
  TableCell,
  TableHead,
  TableRow,
} from "common/components/ui/TableV2";
import type { Member } from "common/types";

import { formatBillingDate } from "../../../utils";
import MemberActions from "../MemberActions";
import MemberActionsDropdown from "../MemberActionsDropdown";
import MemberStatusTag from "../MemberStatusTag";

export default function DesktopTable({ members }: { members: Member[] }) {
  const [isOpen, setIsOpen] = useState(false);
  const [action, setAction] = useState<"remove" | "revoke" | "permissions">();
  const [selectedMember, setSelectedMember] = useState<Member>();

  return (
    <>
      <Table>
        <thead>
          <tr>
            <TableHead>Member's name</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Date Added</TableHead>
            <TableHead>Status</TableHead>
            <TableHead />
          </tr>
        </thead>

        <tbody>
          {members.map((member) => {
            const {
              token,
              first_name: firstName = "New",
              last_name: lastName = "Member",
              role,
              customer_id: memberId,
              email,
              status,
              created_at: createdAt,
              permissions,
            } = member;

            const isPending = status === "pending";

            return (
              <TableRow key={token}>
                <TableCell className="flex items-center gap-2.5">
                  {status === "pending" ? (
                    <ProfileImage src="" />
                  ) : (
                    <AimfoxAvatar firstName={firstName} lastName={lastName} />
                  )}

                  <div className="flex flex-col items-start gap-1">
                    <span
                      className={clsx(
                        "text-md-bold",
                        isPending ? "text-black-400" : "text-black-950",
                      )}
                    >
                      {memberId
                        ? `${firstName} ${lastName}`
                        : `New ${role === "member" ? "Member" : "Admin"}`}
                    </span>

                    <span
                      className={clsx(
                        "inline-block max-w-[40ch] overflow-hidden text-ellipsis text-xs-medium",
                        isPending ? "text-black-400" : "text-black-500",
                      )}
                    >
                      {email}
                    </span>
                  </div>
                </TableCell>

                <TableCell>
                  <MemberPermissionsTag permissions={permissions} role={role} />
                </TableCell>

                <TableCell
                  className={clsx(
                    "text-button-xs",
                    isPending ? "text-black-500" : "text-black-950",
                  )}
                >
                  {role === "owner" ? "-" : formatBillingDate(createdAt)}
                </TableCell>

                <TableCell>
                  <MemberStatusTag
                    memberStatus={status}
                    memberRole={role}
                    size="sm"
                  />
                </TableCell>

                <TableCell>
                  {member.role !== "owner" && (
                    <MemberActionsDropdown
                      member={member}
                      setAction={(newAction) => {
                        setSelectedMember(member);
                        setAction(newAction);
                        setIsOpen(true);
                      }}
                    />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </tbody>
      </Table>

      {selectedMember && (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent size={action === "permissions" ? "lg" : "md"}>
            <MemberActions
              action={action}
              member={selectedMember}
              onClose={() => setIsOpen(false)}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
